import { logger } from "@/utils/logger";
import { useRef, useCallback, useEffect, useState } from 'react';
import { securedSupabase } from '@/integrations/supabase/secured-client';
import { saveRecentAPICall, saveRecentFullResponse } from '@/utils/recentResponseUtils';
import { useLanguage } from '@/contexts/LanguageContext';
import { getUTMSource, getUTMCampaign } from '@/utils/cookieUtils';
import { toast } from 'sonner';
// New hook for OpenAI Responses API
export const useOpenAIResponses = () => {
  const { t, language } = useLanguage();
  const pendingRequestRef = useRef<Promise<string> | null>(null);
  const lastRequestHashRef = useRef<string>('');
  const conversationRef = useRef<Array<{role: 'user' | 'assistant', content: string}>>([]);
  const lastResponseIdRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const initializedRef = useRef(false);
  const lastFailedRequestRef = useRef<{
    message: string;
    sessionId?: string;
    inventoryFilter?: any;
    l1Title?: string | null;
    showroomFilter?: any;
  } | null>(null);
  const [incompleteUserMessage, setIncompleteUserMessage] = useState<string | null>(null);

  // Request deduplication - prevent identical calls
  const createRequestHash = (message: string, dealerId: string) => {
    return `${dealerId}_${message.slice(0, 100)}`;
  };

  // CRITICAL: Restore conversation state from SecureStorage on mount
  useEffect(() => {
    const restoreState = async () => {
      logger.log('🔵 useOpenAIResponses: Hook initialized/mounted');
      logger.log('🔵 initializedRef.current:', initializedRef.current);
      logger.log('🔵 Current timestamp:', new Date().toISOString());
      
      // Import SecureStorage
      const SecureStorage = (await import('@/utils/secureStorage')).default;

      // CRITICAL: Check tab-scoped storage FIRST to avoid cross-tab interference
      const savedResponseIdTab = sessionStorage.getItem('openai_response_id_tab');
      const savedResponseIdSync = SecureStorage.getItemSync('openai_response_id');
      const savedResponseIdDirect = localStorage.getItem('openai_response_id_direct');
      // Use tab-scoped ID first, then fall back to shared storage
      const effectiveResponseId = savedResponseIdTab || savedResponseIdSync || savedResponseIdDirect;
      const savedChatMessages =
        sessionStorage.getItem('d2c_chat_messages') ||
        localStorage.getItem('d2c_chat_messages_backup');

      // CRITICAL FIX: Always check if refs need restoration, even if "initialized" flag is set
      // This handles cases where component unmounted/remounted but refs were cleared
      const needsRestoration = !lastResponseIdRef.current || conversationRef.current.length === 0;
      const hasSecureData = savedResponseIdSync || savedResponseIdDirect || savedChatMessages;

      if (!needsRestoration && initializedRef.current) {
        logger.log('🔵 Hook already initialized AND refs are valid, skipping restoration');
        logger.log('  - lastResponseIdRef.current:', lastResponseIdRef.current);
        return;
      }
      
      if (needsRestoration && hasSecureData) {
        logger.log('🔄🔄🔄 CRITICAL: Refs are empty but SecureStorage has data - RESTORING NOW!');
      }
      
      initializedRef.current = true;
      
      logger.log('🔄 STARTING RESTORATION from SecureStorage...');
      
      try {
        const navigationPending = sessionStorage.getItem('classification_navigation_pending');
        const requestInProgress = sessionStorage.getItem('openai_request_in_progress');

        logger.log('📦 Storage contents:');
        logger.log('  - sessionStorage openai_response_id_tab (TAB-SCOPED):', savedResponseIdTab ? `YES (${savedResponseIdTab.substring(0, 20)}...)` : 'NO');
        logger.log('  - SecureStorage openai_response_id:', savedResponseIdSync ? `YES (${savedResponseIdSync.substring(0, 20)}...)` : 'NO');
        logger.log('  - localStorage openai_response_id_direct:', savedResponseIdDirect ? `YES (${savedResponseIdDirect.substring(0, 20)}...)` : 'NO');
        logger.log('  - effectiveResponseId (used):', effectiveResponseId ? `YES (${effectiveResponseId.substring(0, 20)}...)` : 'NO');
        logger.log('  - chat messages (sessionStorage/localStorage backup):', savedChatMessages ? `YES (${JSON.parse(savedChatMessages).length} messages)` : 'NO');
        logger.log('  - request in progress:', requestInProgress ? 'YES' : 'NO');

        if (savedChatMessages) {
          const parsed = JSON.parse(savedChatMessages);
          
          // CRITICAL: Check if request was interrupted (user message but no AI response)
          const lastMessage = parsed[parsed.length - 1];
          if (requestInProgress && lastMessage?.role === 'user') {
            logger.warn('⚠️ DETECTED interrupted request - last message was user message');
            // Clear the in-progress flag
            sessionStorage.removeItem('openai_request_in_progress');
            // Set the incomplete message so calling code can resume
            setIncompleteUserMessage(lastMessage.content);
          }
        }
        
        // CRITICAL: Prioritize tab-scoped storage to avoid cross-tab interference
        if (savedResponseIdTab) {
          lastResponseIdRef.current = savedResponseIdTab;
          logger.log('✅ RESTORED: lastResponseIdRef from sessionStorage (TAB-SCOPED):', savedResponseIdTab);
          logger.log('  - This is the preferred source - isolated per tab');
        } else if (savedResponseIdSync) {
          lastResponseIdRef.current = savedResponseIdSync;
          logger.log('✅ RESTORED: lastResponseIdRef from SecureStorage (SYNC):', savedResponseIdSync);
          logger.log('  - Fallback: may be from another tab, use with caution');
        } else if (savedResponseIdDirect) {
          lastResponseIdRef.current = savedResponseIdDirect;
          // Also seed SecureStorage sync slot for future restores
          SecureStorage.setItemSync('openai_response_id', savedResponseIdDirect);
          logger.log('✅ RESTORED: lastResponseIdRef from localStorage (direct):', savedResponseIdDirect);
          logger.log('  - Fallback: may be from another tab, use with caution');
        } else {
          logger.log('⚠️ No saved response ID found in storage');
          logger.log('  - Either this is first message or storage was cleared');
        }

        if (savedChatMessages) {
          conversationRef.current = JSON.parse(savedChatMessages);
        } else {
          logger.log('⚠️ No saved chat messages found in sessionStorage');
          logger.log('  - Either this is first message or storage was cleared');
        }
        
        // Check and clean up old navigation pending flags
        if (navigationPending) {
          const timeSinceNavigation = Date.now() - parseInt(navigationPending);
          logger.log('🔍 Navigation pending flag found, age:', timeSinceNavigation, 'ms');
          if (timeSinceNavigation > 10000) {
            sessionStorage.removeItem('classification_navigation_pending');
          }
        }
      } catch (error) {
        console.error('❌ Failed to restore conversation state:', error);
      }
    };
    
    restoreState();
  }, []);

  const abortCurrentRequest = useCallback(() => {
    // CRITICAL: Don't abort during active navigation
    const navigationPending = sessionStorage.getItem('classification_navigation_pending');
    if (navigationPending) {
      const navigationTime = parseInt(navigationPending);
      const timeSinceNavigation = Date.now() - navigationTime;
      if (timeSinceNavigation < 5000) {
        logger.log('🚫 BLOCKED: Skipping abort during active navigation');
        return;
      }
    }
    
    // CRITICAL: Don't abort if a request is in progress (unless it's truly stale)
    const requestInProgress = sessionStorage.getItem('openai_request_in_progress');
    if (requestInProgress) {
      const requestTime = parseInt(requestInProgress);
      const timeSinceRequest = Date.now() - requestTime;
      // Only abort if request has been running for more than 2 seconds
      // This prevents rapid fire message sending from aborting valid requests
      if (timeSinceRequest < 2000) {
        logger.log('🚫 BLOCKED: Skipping abort - request just started', timeSinceRequest, 'ms ago');
        return;
      }
    }
    
    if (abortControllerRef.current) {
      logger.log('🛑 Aborting current request');
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (pendingRequestRef.current) {
      pendingRequestRef.current = null;
    }
  }, []);

  const sendMessage = useCallback(async (message: string, sessionId?: string, inventoryFilter?: any, l1Title?: string | null, showroomFilter?: any): Promise<string> => {
    // ====== CRITICAL DIAGNOSTIC LOGGING - START OF SENDMESSAGE ======
    logger.log('🚨🚨🚨 ===== SENDMESSAGE CALLED ===== 🚨🚨🚨');
    logger.log('📍 Current state snapshot:');
    logger.log('  - lastResponseIdRef.current:', lastResponseIdRef.current);
    logger.log('  - sessionStorage d2c_openai_response_id:', sessionStorage.getItem('d2c_openai_response_id'));
    logger.log('  - sessionStorage d2c_chat_messages:', sessionStorage.getItem('d2c_chat_messages'));
    logger.log('  - sessionId param:', sessionId);
    logger.log('🚨🚨🚨 ========================== 🚨🚨🚨');
    
    // Get dealer ID and stream preference
    const { DealerManager } = await import('@/utils/dealerManager');
    let dealerId = DealerManager.getCurrentDealerId();
    let dealerData = DealerManager.getCurrentDealer();
    
    if (!dealerId) {
      // Try to initialize if not available
      logger.warn('⚠️ Dealer ID not cached, attempting to initialize...');
      try {
        const config = await DealerManager.initializeFromGlobalSettings();
        if (!config?.dealer_id) {
          throw new Error('No dealer ID available after initialization');
        }
        // Update dealerId and dealerData after successful initialization
        dealerId = config.dealer_id;
        dealerData = config;
        logger.log('✅ Dealer initialized successfully, ID:', dealerId);
      } catch (error) {
        console.error('❌ Failed to initialize dealer:', error);
        throw new Error('No dealer ID available');
      }
    }

    // Get session ID if not provided
    let finalSessionId = sessionId;
    if (!finalSessionId) {
      const { sessionManager } = await import('@/utils/sessionManager');
      const currentSession = sessionManager.getCurrentSession();
      finalSessionId = currentSession?.sessionId;
    }

    // Track conversation state for debugging
    logger.log('🔍 CONVERSATION STATE CHECK:');
    logger.log('  - lastResponseIdRef.current:', lastResponseIdRef.current);
    
    // Check storage SYNCHRONOUSLY for response_id
    const SecureStorage = (await import('@/utils/secureStorage')).default;
    const secureResponseId = SecureStorage.getItemSync('openai_response_id');
    const directResponseId = localStorage.getItem('openai_response_id_direct');
    logger.log('  - SecureStorage openai_response_id (SYNC):', secureResponseId);
    logger.log('  - localStorage openai_response_id_direct:', directResponseId);
    logger.log('  - sessionStorage d2c_chat_messages count:', JSON.parse(sessionStorage.getItem('d2c_chat_messages') || '[]').length);

    // If ref is empty but storage has it, restore immediately
    if (!lastResponseIdRef.current && (secureResponseId || directResponseId)) {
      logger.log('🔄 EMERGENCY RESTORE: Found response_id in storage but ref was empty!');
      lastResponseIdRef.current = secureResponseId || directResponseId;
    }

    // Note: We intentionally do NOT clear lastResponseIdRef based on conversationRef length
    // The response_id should persist for conversation continuity even if conversationRef is cleared
    logger.log('  - lastResponseIdRef.current (after restore check):', lastResponseIdRef.current);
    logger.log('  - response_id will be sent if available to maintain context');

    // COST OPTIMIZATION: Request deduplication
    const requestHash = createRequestHash(message, String(dealerId || 'unknown'));
    if (requestHash === lastRequestHashRef.current && pendingRequestRef.current) {
      return pendingRequestRef.current;
    }

    // Abort any existing request
    abortCurrentRequest();

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    lastRequestHashRef.current = requestHash;
    
    // Store request parameters in case we need to retry
    lastFailedRequestRef.current = {
      message,
      sessionId: finalSessionId,
      inventoryFilter,
      l1Title,
      showroomFilter
    };

    // Mark request as in progress IMMEDIATELY before making the API call
    sessionStorage.setItem('openai_request_in_progress', Date.now().toString());
    logger.log('🔵 MARKED: OpenAI request in progress');

    // Start parallel data loading (non-blocking) - data will be ready when response arrives
    import('@/utils/parallelDataLoader').then(({ startParallelDataLoading }) => {
      startParallelDataLoading();
    }).catch(() => {
      // Parallel loader not available - hydration will use fallback
    });

    // Create the actual request promise
    const requestPromise = (async (): Promise<string> => {
      try {
        // Get current date/time for user prompt template
        const currentDateTime = new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZoneName: 'short',
        });

        // Get page context for user prompt template - use document.title (meta title)
        let currentPageMetaTitle = '';
        let currentSiteDomain = dealerData?.domain || window.location.hostname;
        let specsVinContent = '';
        
        // Primary: use the page's <title> tag (meta title)
        if (typeof document !== 'undefined') {
          try {
            currentPageMetaTitle = document.title || '';
            
            // Try to get specsVin content if it exists on the page
            const specsVinElement = document.getElementById('specsVin');
            if (specsVinElement?.textContent?.trim()) {
              specsVinContent = specsVinElement.textContent.trim();
            }
          } catch {
            // Document access failed (non-critical)
          }
        }

        // Fallback: derive from URL if no meta title available
        if (!currentPageMetaTitle && typeof window !== 'undefined') {
          try {
            const { hostname, pathname } = window.location;
            currentPageMetaTitle = pathname === '/' ? hostname : `${hostname}${pathname}`;
          } catch {
            // URL fallback failed (non-critical)
          }
        }

        logger.log('🧩 User prompt context variables (client):', {
          currentDateTime,
          currentPageMetaTitle,
          currentSiteDomain,
          specsVinContent: specsVinContent || '(not found)',
        });

        // Prepare the API call payload - now only sending previous_response_id, not full conversation history
        
        // Check for test mode parameter
        const urlParams = new URLSearchParams(window.location.search);
        const testMode = urlParams.get('chatbottest') === 'true';
        
        // Get dealer address from localStorage
        let dealerAddress = '';
        try {
          const addressData = localStorage.getItem('Dealer_Address');
          if (addressData) {
            const address = JSON.parse(addressData);
            dealerAddress = `${address.street}, ${address.city}, ${address.province}, ${address.postal_code}`;
          }
        } catch (error) {
          // Dealer address retrieval failed (non-critical)
        }
        
        // Get VDP JSON data from localStorage
        let vdpJsonData = null;
        try {
          const vdpData = localStorage.getItem('VDPjsonData');
          if (vdpData) {
            vdpJsonData = JSON.parse(vdpData);
          }
        } catch (error) {
          // VDP JSON data retrieval failed (non-critical)
        }
        
        // Get Showroom VDP JSON data from localStorage
        let vdpShowroomJsonData = null;
        try {
          const vdpShowroomData = localStorage.getItem('VDPShowroomjsonData');
          if (vdpShowroomData) {
            vdpShowroomJsonData = JSON.parse(vdpShowroomData);
          }
        } catch (error) {
          // Showroom VDP JSON data retrieval failed (non-critical)
        }
        
        // Get dealer phones from localStorage
        let dealerPhones = { sales: '', service: '', parts: '' };
        try {
          const phonesData = localStorage.getItem('Dealer_Phones');
          if (phonesData) {
            dealerPhones = JSON.parse(phonesData);
          }
        } catch (error) {
          // Dealer phones retrieval failed (non-critical)
        }
        
        // Get dealer hours from localStorage
        let dealerHours = '';
        try {
          const hoursData = localStorage.getItem('Dealer_Hours');
          if (hoursData) {
            dealerHours = hoursData;
          }
        } catch (error) {
          // Dealer hours retrieval failed (non-critical)
        }
        
        // Get UTM campaign data from cookies for SMS tool (ignore Google organic)
        const utmSource = getUTMSource();
        const utmCampaign = getUTMCampaign();
        const isGoogleOrganic = utmSource?.toLowerCase() === 'google' && utmCampaign?.toLowerCase() === 'organic';
        
        // Check if EV Boost API is available and ready on the page
        let evBoostData = undefined;
        if (typeof window !== 'undefined' && (window as any).EVBoostAPI?.isReady?.()) {
          try {
            evBoostData = (window as any).EVBoostAPI.getSummary();
            logger.log('⚡ EV Boost data available, including in payload');
          } catch (e) {
            logger.warn('⚠️ Failed to get EV Boost data:', e);
          }
        }

        // Message is sent as-is - context maintained via previous_response_id
        
        const apiCallPayload: any = {
          message, // Context maintained via previous_response_id
          dealer_id: dealerId,
          testMode,
          dealer_address: dealerAddress,
          dealer_phone_sales: dealerPhones.sales,
          dealer_phone_service: dealerPhones.service,
          dealer_phone_parts: dealerPhones.parts,
          dealer_hours: dealerHours,
          dealer_url: window.location.origin, // Add dealer URL for inventory fetch
          vdp_json_data: vdpJsonData, // Add VDP JSON data for system prompt replacement
          vdp_showroom_json_data: vdpShowroomJsonData, // Add Showroom VDP JSON data for system prompt replacement
          // Client-side context variables for user prompt template
          current_date_time: currentDateTime,
          current_page_meta_title: currentPageMetaTitle,
          current_site_domain: currentSiteDomain,
          specs_vin_content: specsVinContent || undefined,
          // UTM parameters from 1st party cookies (for system prompt variables)
          utm_source: utmSource || undefined,
          utm_campaign: utmCampaign || undefined,
          // UTM campaign data for SMS tool (excluding Google organic traffic)
          campaign_source: (!isGoogleOrganic && utmSource) ? utmSource : undefined,
          campaign_name: (!isGoogleOrganic && utmCampaign) ? utmCampaign : undefined,
          // EV Boost calculator data (when available on page)
          ev_boost_data: evBoostData,
          // UI language toggle (EN/FR) — drives AI response language; defaults to 'en' server-side
          app_language: language === 'fr' ? 'fr' : 'en',
          // Only send previous_response_id for conversation continuation
        };

        // Check if this is a voice message
        const isVoiceMessage = sessionStorage.getItem('d2c_is_voice_message') === 'true';
        if (isVoiceMessage) {
          apiCallPayload.is_voice_message = true;
          // Clear the flag after reading it
          sessionStorage.removeItem('d2c_is_voice_message');
        }

        // BELT & SUSPENDERS: Check MULTIPLE sources for previous_response_id
        logger.log('🔍🔍🔍 ========== CHECKING FOR previous_response_id (BELT & SUSPENDERS) ==========');
        logger.log('🔍 Time:', new Date().toISOString());
        
        // Source 1: Ref (fastest, but can be lost on remount)
        const refValue = lastResponseIdRef.current;
        logger.log('🔍 SOURCE 1 (ref):', refValue);
        
        // Source 2: Direct localStorage (most reliable, synchronous)
        const localStorageValue = localStorage.getItem('openai_response_id_direct');
        logger.log('🔍 SOURCE 2 (localStorage direct):', localStorageValue);
        
        // Source 3: SecureStorage (encrypted but might have timing issues)
        const SecureStorageForPayload = (await import('@/utils/secureStorage')).default;
        const secureStorageValue = SecureStorageForPayload.getItemSync('openai_response_id');
        logger.log('🔍 SOURCE 3 (SecureStorage):', secureStorageValue);
        
        // Source 4: Window-level backup
        const windowValue = (window as any).__lastResponseId;
        logger.log('🔍 SOURCE 4 (window backup):', windowValue);
        
        // Pick the first non-null value (priority order: ref > localStorage > SecureStorage > window)
        const finalResponseId = refValue || localStorageValue || secureStorageValue || windowValue || null;
        
        if (finalResponseId) {
          logger.log('✅✅✅ FOUND previous_response_id:', finalResponseId);
          logger.log('✅ Source:', refValue ? 'ref' : localStorageValue ? 'localStorage' : secureStorageValue ? 'SecureStorage' : 'window');
          logger.log('✅✅✅ SENDING TO API - CONVERSATION CONTEXT MAINTAINED');
          apiCallPayload.previous_response_id = finalResponseId;
          
          // BELT: Update ALL storage locations to keep them in sync
          lastResponseIdRef.current = finalResponseId;
          try {
            localStorage.setItem('openai_response_id_direct', finalResponseId);
          } catch (storageError) {
            logger.warn('⚠️ localStorage quota exceeded during sync update');
          }
          (window as any).__lastResponseId = finalResponseId;
        } else {
          logger.log('🟡 No previous_response_id found - this is a new conversation');
        }

        
        // Add inventory filter and L1 title if provided
        if (inventoryFilter) {
          apiCallPayload.inventoryFilter = inventoryFilter;
        }
        
        if (l1Title) {
          apiCallPayload.promptMenuL1 = l1Title;
        }
        
        if (showroomFilter) {
          apiCallPayload.showroomFilter = showroomFilter;
        }
        
        // Clear debug data immediately when sending new message
        const { debugLogger } = await import('@/utils/debugLogger');
        debugLogger.clearDebugData();

        // Import the base supabase client to avoid dealer context headers interfering with edge functions
        const { supabase } = await import('@/integrations/supabase/client');
        
        // Add explicit timeout to prevent hanging after inactivity
        // Set to 95s (slightly longer than edge function's 90s timeout)
        const controller = abortControllerRef.current!;
        const timeoutId = setTimeout(() => {
          console.error('🚨 CLIENT TIMEOUT: Edge function call exceeded 95 seconds');
          controller.abort();
        }, 95000);
        
        // Retry configuration for transient network failures
        const MAX_RETRIES = 3;
        const INITIAL_RETRY_DELAY = 1000; // 1 second
        
        // Helper to check if error is a transient network failure worth retrying
        const isTransientNetworkError = (err: any): boolean => {
          if (!err) return false;
          const msg = err.message?.toLowerCase() || '';
          // Network-level failures that didn't reach the edge function
          return msg.includes('failed to send a request') ||
                 msg.includes('failed to fetch') ||
                 msg.includes('network') ||
                 msg.includes('timeout') ||
                 msg.includes('aborted') ||
                 err.name === 'TypeError'; // fetch network errors are TypeErrors
        };
        
        let lastError: any = null;
        let data: any = null;
        let error: any = null;
        
        for (let attempt = 1; attempt <= MAX_RETRIES; attempt++) {
          try {
            logger.log(`🔄 OpenAI request attempt ${attempt}/${MAX_RETRIES}...`);
            
            const result = await supabase.functions.invoke('openai-responses', {
              body: apiCallPayload,
              headers: finalSessionId ? {
                'x-session-id': finalSessionId
              } : {}
            });
            
            data = result.data;
            error = result.error;
            
            // If we got a response (success or edge function error), break out - don't retry
            // Only retry on network-level failures where request didn't reach the function
            if (!isTransientNetworkError(error)) {
              if (attempt > 1) {
                logger.log(`✅ OpenAI request succeeded on attempt ${attempt}`);
              }
              break;
            }
            
            // It's a transient network error - log and potentially retry
            lastError = error;
            logger.warn(`⚠️ Attempt ${attempt} failed with transient error:`, error?.message);
            
            if (attempt < MAX_RETRIES) {
              const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1); // Exponential backoff: 1s, 2s, 4s
              logger.log(`⏳ Waiting ${delay}ms before retry...`);
              await new Promise(resolve => setTimeout(resolve, delay));
            }
          } catch (invokeErr: any) {
            // Catch-level errors (thrown exceptions, not returned errors)
            if (isTransientNetworkError(invokeErr)) {
              lastError = invokeErr;
              logger.warn(`⚠️ Attempt ${attempt} threw transient error:`, invokeErr?.message);
              
              if (attempt < MAX_RETRIES) {
                const delay = INITIAL_RETRY_DELAY * Math.pow(2, attempt - 1);
                logger.log(`⏳ Waiting ${delay}ms before retry...`);
                await new Promise(resolve => setTimeout(resolve, delay));
                continue;
              }
            }
            // Non-transient error or max retries reached - re-throw
            throw invokeErr;
          }
        }
        
        // If all retries exhausted and we still have a transient error, use lastError
        if (isTransientNetworkError(error) && lastError) {
          console.error(`❌ All ${MAX_RETRIES} attempts failed with transient errors`);
          error = lastError;
        }
        
        try {
          
          clearTimeout(timeoutId);

         // If the edge function responded with non-2xx, surface full context details
         if (error) {
           const anyErr: any = error as any;
           const ctx: any = anyErr?.context || {};
           
           // Extract status code and response body from error context
           const statusCode = ctx.status || ctx.statusCode;
           let responseBody: any = ctx.body;
           
           // Parse response body if it's a stream or string
           try {
             if (responseBody && typeof (responseBody as any).getReader === 'function') {
               const raw = await new Response(responseBody as ReadableStream).text();
               try { responseBody = JSON.parse(raw); } catch { responseBody = { rawText: raw }; }
             } else if (typeof responseBody === 'string') {
               try { responseBody = JSON.parse(responseBody); } catch { responseBody = { rawText: responseBody }; }
             }
           } catch { /* ignore parsing errors */ }
           
           // Send error alert with full diagnostic details
           // Note: We only reach here after all retry attempts have been exhausted
           try {
              const { ErrorAlertService } = await import('@/utils/errorAlertService');
              const { DealerManager } = await import('@/utils/dealerManager');
              
              const dealerId = DealerManager.getCurrentDealerId();
              if (dealerId) {
                const isNetworkError = isTransientNetworkError(anyErr);
                await ErrorAlertService.alertApiError(
                  dealerId, 
                  `OpenAI API Error: ${anyErr.message || 'Edge Function returned a non-2xx status code'}${isNetworkError ? ` (after ${MAX_RETRIES} retry attempts)` : ''}`, 
                  {
                    functionName: 'openai-responses',
                    userMessage: message,
                    sessionId: finalSessionId,
                    statusCode,
                    responseBody,
                    requestPayload: {
                      conversationLength: conversationRef.current.length,
                      hasResponseId: !!lastResponseIdRef.current,
                      promptMenuL1: l1Title,
                      showroomFilter,
                      retryAttempts: MAX_RETRIES,
                      wasNetworkError: isNetworkError
                    },
                    additionalInfo: {
                      error_code: anyErr.code,
                      error_message: anyErr.message,
                      has_context: !!ctx,
                      retry_info: isNetworkError ? `Failed after ${MAX_RETRIES} attempts with exponential backoff` : 'No retries (non-transient error)'
                    }
                  }
                );
              }
             } catch (alertError) {
               console.error('Failed to send error alert:', alertError);
             }
            
            // Force complete app reload on HTTP 500 errors (fatal edge function errors)
            if (statusCode === 500) {
              console.error('🔄 HTTP 500 detected - forcing complete app reload...');
              toast.error('Connection error - reloading app...');
              setTimeout(() => {
                window.location.reload();
              }, 1000);
              return '';
            }
            
            const ctxCopy = ctx;
          let ctxBody: any = ctxCopy.body ?? null;

          // Normalize body to JSON when it's a ReadableStream or string
          try {
            if (ctxBody && typeof (ctxBody as any).getReader === 'function') {
              const raw = await new Response(ctxBody as ReadableStream).text();
              try { ctxBody = JSON.parse(raw); } catch { ctxBody = { message: raw }; }
            } else if (typeof ctxBody === 'string') {
              try { ctxBody = JSON.parse(ctxBody); } catch { /* ignore */ }
            }
          } catch { /* ignore */ }
          // Capture debug info from error context when available
          const dbg = ctxBody?.debug_info;
          if (dbg) {
            if (dbg.actual_openai_payload) {
              debugLogger.logApiCall(dbg.actual_openai_payload);
              await saveRecentAPICall(dbg.actual_openai_payload);
            }
            if (dbg.full_openai_response) {
              debugLogger.logApiResponse(dbg.full_openai_response);
              await saveRecentFullResponse(dbg.full_openai_response);
            }
          }

          // Log full edge error body for the Debug modal
          debugLogger.logApiError(ctxBody || { message: anyErr?.message || 'Unknown error' });

          // Handle specific API key reset case gracefully
          if (ctxBody && ctxBody.code === 'API_KEY_NEEDS_RESET') {
            logger.warn('⚠️ OpenAI API key needs to be reset - returning helpful message instead of throwing error');
            return t('error.ai.configuration.issue');
          }
          
          // Mark this as a retryable error for non-2xx status codes
          const edgeMsg = (ctxBody && (ctxBody.error || ctxBody.message)) || anyErr?.message;
          const errorObj: any = new Error(edgeMsg || t('error.edge.function.failed'));
          errorObj.isRetryable = true;
          throw errorObj;
        }

        // ALWAYS capture debug info first (before handling data validation)
        if (data && data.debug_info) {
          if (data.debug_info.actual_openai_payload) {
            debugLogger.logApiCall(data.debug_info.actual_openai_payload);
            await saveRecentAPICall(data.debug_info.actual_openai_payload);
          }
          if (data.debug_info.full_openai_response) {
            debugLogger.logApiResponse(data.debug_info.full_openai_response);
            await saveRecentFullResponse(data.debug_info.full_openai_response);
          }
          if (data.debug_info.warnings) {
            debugLogger.logApiError({ warnings: data.debug_info.warnings, timestamp: new Date().toISOString() });
          }
        }

        if (!data.success || !data.response) {
          throw new Error('Invalid OpenAI Responses API response');
        }

        // Update conversation history with assistant response
        // Note: User message already saved synchronously in useChat.sendMessage
        // Skip "Close" responses (used to minimize chat)
        const trimmedResponse = data.response.trim().replace(/[.,!?;:]$/, '');
        if (trimmedResponse !== 'Close') {
          conversationRef.current.push({ role: 'user', content: message }, { role: 'assistant', content: data.response });
        } else {
          logger.log('🚪 Skipping conversation history for "Close" response');
          // Still add user message to history, just not the Close response
          conversationRef.current.push({ role: 'user', content: message });
        }

        // CRITICAL: Save AI response to sessionStorage IMMEDIATELY
        // But skip saving "Close" responses (used to minimize chat)
          try {
            const trimmedResponse = data.response.trim().replace(/[.,!?;:]$/, '');
            if (trimmedResponse !== 'Close') {
              const existingMessages = sessionStorage.getItem('d2c_chat_messages');
              const chatMessages = existingMessages ? JSON.parse(existingMessages) : [];
              chatMessages.push({ role: 'assistant', content: data.response });
              sessionStorage.setItem('d2c_chat_messages', JSON.stringify(chatMessages));

              // Backup copy for persistence
              try {
                localStorage.setItem('d2c_chat_messages_backup', JSON.stringify(chatMessages));
              } catch {
                // Non-fatal
              }

              logger.log('💾 IMMEDIATE SAVE: AI response saved to sessionStorage, total messages:', chatMessages.length);
            } else {
              logger.log('🚪 Skipping sessionStorage save for "Close" response');
            }
          } catch (error) {
            console.error('❌ Failed to immediately save AI response:', error);
          }

        // Handle leads generated by backend send_lead tool (includes real DB lead IDs)
        if (Array.isArray((data as any)?.generated_leads) && (data as any).generated_leads.length > 0) {
          try {
            const { shouldUseShiftDigitalTracking, trackShiftChatLead, determineProfitCenter } = await import('../utils/shiftDigitalTracking');
            const { DealerManager } = await import('@/utils/dealerManager');
            const dealer = DealerManager.getCurrentDealer();

            if (shouldUseShiftDigitalTracking(dealer?.certification_name)) {
              const trackedLeadIds = new Set<string>(JSON.parse(sessionStorage.getItem('d2c_shift_tracked_leads') || '[]'));

              for (const lead of (data as any).generated_leads) {
                const leadId = typeof lead?.leadId === 'string' ? lead.leadId : '';
                if (!leadId || trackedLeadIds.has(leadId)) continue;

                const profitCenter = determineProfitCenter(lead?.department);
                const trackingSessionId = (lead?.sessionId as string) || sessionId || `temp_${Date.now()}`;
                trackShiftChatLead(trackingSessionId, leadId, profitCenter);
                trackedLeadIds.add(leadId);
                logger.log('🔵 [SHIFT] chatLead fired from generated_leads with DB leadId:', leadId);
              }

              sessionStorage.setItem('d2c_shift_tracked_leads', JSON.stringify([...trackedLeadIds]));
            }
          } catch (shiftError) {
            logger.warn('🔵 [SHIFT] generated_leads tracking error (non-fatal):', shiftError);
          }
        }

        // ========== AUTO LEAD SUBMISSION - Process Contact data from AI response ==========
        try {
          logger.log('🎯🎯🎯 [AUTO LEAD] Processing response for auto-lead...');
          const { parseAIResponse } = await import('../utils/aiResponseParser');
          const { AutoLeadSubmission } = await import('../utils/autoLeadSubmission');
          const { sessionManager } = await import('../utils/sessionManager');
          
          const parsedResponse = parseAIResponse(data.response);
          
          // Store conversation summary if present
          if (parsedResponse?.Answer?.Conversation_Summary) {
            sessionManager.updateConversationSummary(parsedResponse.Answer.Conversation_Summary);
            logger.log('🎯🎯🎯 [AUTO LEAD] Conversation summary stored');
          }
          
          const hasContact = !!parsedResponse?.Contact;
          logger.log('🎯🎯🎯 [AUTO LEAD] Has Contact object?', hasContact);
          
          // NOTE: Shift Digital chatLead event is now fired AFTER successful lead submission
          // (not on phone/email detection) to use the real lead ID from the database
          
          if (hasContact) {
            const isValidContact = AutoLeadSubmission.hasValidContactData(parsedResponse.Contact);
            
            if (isValidContact) {
              // PHONE VALIDATION: Ensure phone was provided by user and isn't a dealer phone
              const { validatePhoneForLead } = await import('../utils/phoneLeadValidation');
              const phoneValidation = validatePhoneForLead(parsedResponse.Contact.Phone, conversationRef.current);
              
              if (!phoneValidation.valid) {
                const hasEmail = !!parsedResponse.Contact.Email && parsedResponse.Contact.Email.trim().length > 0;
                if (!hasEmail) {
                  logger.log(`🎯🎯🎯 [AUTO LEAD] ❌ Skipping lead - ${phoneValidation.reason} and no email provided`);
                } else {
                  logger.log(`🎯🎯🎯 [AUTO LEAD] ⚠️ ${phoneValidation.reason} - continuing with email only`);
                  parsedResponse.Contact.Phone = '';
                }
              }

              const hasValidContactAfterPhoneCheck = parsedResponse.Contact.Phone?.trim() || parsedResponse.Contact.Email?.trim();
              if (!hasValidContactAfterPhoneCheck) {
                logger.log('🎯🎯🎯 [AUTO LEAD] ❌ No valid contact method remaining after phone validation');
              } else {
              // CRITICAL: Ensure we have a session ID - try multiple sources
              let currentSessionId = sessionId;
              
              if (!currentSessionId) {
                const currentSession = sessionManager.getCurrentSession();
                currentSessionId = currentSession?.sessionId;
              }
              
              // If still no session ID, try to initialize one
              if (!currentSessionId) {
                logger.warn('🎯🎯🎯 [AUTO LEAD] ⚠️ No session ID found, initializing new session...');
                const newSession = sessionManager.initializeSession();
                currentSessionId = newSession?.sessionId;
              }
              
              // Final fallback: generate a temporary session ID for lead tracking
              if (!currentSessionId) {
                currentSessionId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
                logger.warn('🎯🎯🎯 [AUTO LEAD] ⚠️ Using temporary session ID:', currentSessionId);
              }
              
              logger.log('🎯🎯🎯 [AUTO LEAD] Using session ID:', currentSessionId);
              
              const alreadySubmitted = await AutoLeadSubmission.hasBeenSubmitted(
                currentSessionId,
                parsedResponse.Contact
              );
              logger.log('🎯🎯🎯 [AUTO LEAD] Already submitted?', alreadySubmitted);
              
              if (!alreadySubmitted) {
                logger.log('🎯🎯🎯 [AUTO LEAD] ✅ Triggering auto-lead submission...');
                
                const { processAutoLeadFromContact } = await import('../utils/autoLeadFromContact');
                
                // Merge Reply_Message.Intro and Closing into Interest data
                const enrichedInterest = parsedResponse.Interest ? {
                  ...parsedResponse.Interest,
                  intro: parsedResponse.Reply_Message?.Intro,
                  closing: parsedResponse.Reply_Message?.Closing
                } : {
                  intro: parsedResponse.Reply_Message?.Intro,
                  closing: parsedResponse.Reply_Message?.Closing
                };
                
                const result = await processAutoLeadFromContact(
                  parsedResponse.Contact,
                  enrichedInterest
                );
                logger.log('🎯🎯🎯 [AUTO LEAD] ✅✅✅ Auto-lead submission result:', result);
                
                // Fire Shift Digital chatLead with real lead ID from database
                if (result.success && result.leadId) {
                  try {
                    const { shouldUseShiftDigitalTracking, trackShiftChatLead, determineProfitCenter } = await import('../utils/shiftDigitalTracking');
                    const { DealerManager } = await import('@/utils/dealerManager');
                    const dealer = DealerManager.getCurrentDealer();
                    if (shouldUseShiftDigitalTracking(dealer?.certification_name)) {
                      const department = parsedResponse.Interest?.department;
                      const profitCenter = determineProfitCenter(department);
                      const trackingSessionId = sessionId || `temp_${Date.now()}`;
                      trackShiftChatLead(trackingSessionId, result.leadId, profitCenter);
                      logger.log('🔵 [SHIFT] chatLead fired after auto-lead with real DB leadId:', result.leadId);
                    }
                  } catch (shiftError) {
                    logger.warn('🔵 [SHIFT] chatLead tracking error (non-fatal):', shiftError);
                  }
                }
              } else {
                logger.log('🎯🎯🎯 [AUTO LEAD] ❌ Skipping - already submitted in this session');
              }
              } // end hasValidContactAfterPhoneCheck
            }
          }
        } catch (autoLeadError) {
          console.error('❌ AUTO LEAD processing error (non-fatal):', autoLeadError);
          // Don't throw - auto-lead failure shouldn't break the chat flow
        }

        // BELT & SUSPENDERS: Store response ID in MULTIPLE locations IMMEDIATELY
        if (data.response_id) {
          const responseId = data.response_id;
          const timestamp = new Date().toISOString();
          
          logger.log('🟢🟢🟢 ========== STORING RESPONSE_ID (BELT & SUSPENDERS) ==========');
          logger.log('🟢 response_id:', responseId);
          logger.log('🟢 timestamp:', timestamp);
          
          // IMMEDIATE STORAGE - All synchronous, no delays
          
          // 1. Ref (fastest access)
          lastResponseIdRef.current = responseId;
          logger.log('🟢 STEP 1: Saved to lastResponseIdRef.current');
          
          // 2. Tab-scoped sessionStorage (CRITICAL for same-domain navigation)
          try {
            sessionStorage.setItem('openai_response_id_tab', responseId);
            sessionStorage.setItem('openai_response_id_tab_timestamp', timestamp);
            logger.log('🟢 STEP 2: Saved to sessionStorage (TAB-SCOPED)');
          } catch (sessionError) {
            logger.warn('⚠️ sessionStorage failed');
          }
          
          // 3. Direct localStorage (most reliable, persists across reloads)
          try {
            localStorage.setItem('openai_response_id_direct', responseId);
            localStorage.setItem('openai_response_id_timestamp', timestamp);
            logger.log('🟢 STEP 3: Saved to localStorage (direct)');
          } catch (storageError) {
            logger.warn('⚠️ localStorage quota exceeded, clearing expendable data and retrying...');
            // Import and use quota cleanup
            const { clearLocalStorageForQuota } = await import('@/utils/localStorageCleanup');
            clearLocalStorageForQuota();
            // Retry after cleanup
            try {
              localStorage.setItem('openai_response_id_direct', responseId);
              localStorage.setItem('openai_response_id_timestamp', timestamp);
              logger.log('🟢 STEP 3: Saved to localStorage after cleanup');
            } catch (retryError) {
              logger.warn('⚠️ localStorage still full after cleanup, skipping direct storage');
            }
          }
          
          // 4. Window-level backup (accessible globally)
          (window as any).__lastResponseId = responseId;
          (window as any).__lastResponseIdTimestamp = timestamp;
          logger.log('🟢 STEP 4: Saved to window.__lastResponseId');
          
          // 5. SecureStorage (encrypted, existing mechanism)
          const SecureStorage = (await import('@/utils/secureStorage')).default;
          SecureStorage.setItemSync('openai_response_id', responseId);
          logger.log('🟢 STEP 5: Saved to SecureStorage');
          
          // VERIFICATION - Read back from all sources
          const refCheck = lastResponseIdRef.current;
          const sessionCheck = sessionStorage.getItem('openai_response_id_tab');
          const localStorageCheck = localStorage.getItem('openai_response_id_direct');
          const windowCheck = (window as any).__lastResponseId;
          const secureCheck = SecureStorage.getItemSync('openai_response_id');
          
          logger.log('🟢 ========== VERIFICATION ==========');
          logger.log('🟢 Ref matches?', refCheck === responseId, '(', refCheck, ')');
          logger.log('🟢 sessionStorage (TAB) matches?', sessionCheck === responseId, '(', sessionCheck, ')');
          logger.log('🟢 localStorage matches?', localStorageCheck === responseId, '(', localStorageCheck, ')');
          logger.log('🟢 Window matches?', windowCheck === responseId, '(', windowCheck, ')');
          logger.log('🟢 SecureStorage matches?', secureCheck === responseId, '(', secureCheck, ')');
          
          const allMatch = refCheck === responseId && sessionCheck === responseId && localStorageCheck === responseId && windowCheck === responseId && secureCheck === responseId;
          if (allMatch) {
            logger.log('✅✅✅ PERFECT: All 5 storage locations verified!');
            logger.log('✅ response_id will be available for NEXT message');
          } else {
            logger.log('⚠️⚠️⚠️ WARNING: Not all storage locations match!');
          }
        } else {
          logger.log('🔴🔴🔴 CRITICAL: API response did NOT contain response_id!');
          logger.log('🔴 Full response keys:', Object.keys(data));
        }

        // Clear the in-progress flag - request completed successfully
        sessionStorage.removeItem('openai_request_in_progress');
        logger.log('✅ CLEARED: OpenAI request completed successfully');

        return data.response;
        } catch (invokeError: any) {
          clearTimeout(timeoutId);
          
          // Clear the in-progress flag on error
          sessionStorage.removeItem('openai_request_in_progress');
          logger.log('❌ CLEARED: OpenAI request failed');
          
          // Check if this was an abort/timeout error
          if (invokeError.name === 'AbortError' || invokeError.message?.includes('aborted')) {
            // Send timeout alert email
            try {
              const { supabase } = await import('@/integrations/supabase/client');
              const { DealerManager } = await import('@/utils/dealerManager');
              
              const dealerId = DealerManager.getCurrentDealerId();
              const dealerData = DealerManager.getCurrentDealer();
              
              await supabase.functions.invoke('send-timeout-alert', {
                body: {
                  message: message,
                  dealerId: dealerId?.toString(),
                  dealerName: dealerData?.dealer_name || 'Unknown',
                  siteUrl: window.location.origin,
                  sessionId: finalSessionId,
                  openaiSessionId: lastResponseIdRef.current || sessionStorage.getItem('openai_response_id_tab') || null,
                  timestamp: new Date().toISOString(),
                  errorMessage: 'OpenAI API request timed out. Please try again.',
                  userAgent: navigator.userAgent
                }
              });
            } catch (emailError) {
              console.error('Failed to send timeout alert email:', emailError);
            }
            
            throw new Error('Request timed out. This can happen after periods of inactivity. Please try again.');
          }
          
          // Re-throw other errors to be handled by outer catch
          throw invokeError;
        }
      } catch (error) {
        console.error('❌ OpenAI Responses API request failed:', error);
        throw error;
      } finally {
        // Clear the pending request
        pendingRequestRef.current = null;
        lastRequestHashRef.current = '';
      }
    })();

    // Store the pending request for deduplication
    pendingRequestRef.current = requestPromise;
    
    return requestPromise;
  }, [abortCurrentRequest, t]);

  const clearConversation = useCallback(async () => {
    logger.log('🔴 clearConversation called');
    console.trace('🔴 Call stack for clearConversation');
    
    // CRITICAL: Check if navigation is in progress - don't clear during navigation
    const navigationPending = sessionStorage.getItem('classification_navigation_pending');
    if (navigationPending) {
      const navigationTime = parseInt(navigationPending);
      const timeSinceNavigation = Date.now() - navigationTime;
      logger.log('🔍 Navigation check:', { navigationTime, timeSinceNavigation, willBlock: timeSinceNavigation < 10000 });
      // If navigation started in last 10 seconds, preserve conversation
      if (timeSinceNavigation < 10000) {
        logger.log('🚫 BLOCKED: Skipping clearConversation during active navigation (within 10s window)');
        return;
      } else {
        logger.log('✅ Navigation window expired, proceeding with clear');
        // Navigation completed, clear the flag
        sessionStorage.removeItem('classification_navigation_pending');
      }
    } else {
      logger.log('ℹ️ No navigation pending flag found');
    }
    
    logger.log('🧹 CLEARING: OpenAI conversation and response ID from ALL storage locations');
    logger.log('🧹 Before clear - lastResponseIdRef:', lastResponseIdRef.current);
    
    // CRITICAL: Clear conversation state IMMEDIATELY and SYNCHRONOUSLY
    conversationRef.current = [];
    lastResponseIdRef.current = null;
    lastRequestHashRef.current = '';
    pendingRequestRef.current = null;
    
    // BELT & SUSPENDERS: Clear from ALL storage locations (4 sources)
    // 1. Direct localStorage
    localStorage.removeItem('openai_response_id_direct');
    localStorage.removeItem('openai_response_id_timestamp');
    logger.log('🧹 Cleared localStorage items');
    
    // 2. Window-level backup
    delete (window as any).__lastResponseId;
    delete (window as any).__lastResponseIdTimestamp;
    logger.log('🧹 Cleared window backup');
    
    // 3. SecureStorage
    const SecureStorage = (await import('@/utils/secureStorage')).default;
    await SecureStorage.removeItem('openai_response_id');
    logger.log('🧹 Cleared SecureStorage items');
    
    // 4. SessionStorage
    sessionStorage.removeItem('d2c_openai_conversation');
    sessionStorage.removeItem('d2c_chat_messages');

    // Also clear localStorage backup copy (if present)
    localStorage.removeItem('d2c_chat_messages_backup');

    logger.log('🧹 Cleared sessionStorage items');

    // Abort any current request
    abortCurrentRequest();
    
    // Double-check that all refs are actually null/empty after clearing
    if (lastResponseIdRef.current !== null) {
      console.error('❌ CRITICAL: lastResponseIdRef was NOT properly cleared!');
      lastResponseIdRef.current = null;
    }
    if (conversationRef.current.length !== 0) {
      console.error('❌ CRITICAL: conversationRef was NOT properly cleared!');
      conversationRef.current = [];
    }
    
    logger.log('✅ Clear complete');
  }, [abortCurrentRequest]);

  const getConversationHistory = useCallback(() => {
    return [...conversationRef.current];
  }, []);

  const getCurrentResponseId = useCallback(() => {
    return lastResponseIdRef.current;
  }, []);
  
  const retryLastRequest = useCallback(async (): Promise<string> => {
    if (!lastFailedRequestRef.current) {
      throw new Error('No failed request to retry');
    }
    
    logger.log('🔄 Retrying last failed request...');
    const { message, sessionId, inventoryFilter, l1Title, showroomFilter } = lastFailedRequestRef.current;
    
    // Clear the failed request ref before retry
    lastFailedRequestRef.current = null;
    
    return sendMessage(message, sessionId, inventoryFilter, l1Title, showroomFilter);
  }, [sendMessage]);

  // Listen for global clear conversation events (e.g., from inactivity timeout)
  useEffect(() => {
    const handleClearConversation = (event: CustomEvent) => {
      logger.log('🔔 Received global clear conversation event:', event.detail);
      clearConversation();
    };

    window.addEventListener('d2c-clear-conversation' as any, handleClearConversation as EventListener);

    return () => {
      window.removeEventListener('d2c-clear-conversation' as any, handleClearConversation as EventListener);
    };
  }, [clearConversation]);

  return {
    sendMessage,
    clearConversation,
    abortCurrentRequest,
    getConversationHistory,
    getCurrentResponseId,
    retryLastRequest,
    incompleteUserMessage
  };
};
